import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';
import { SearchUpdateEvent } from '@shopify/events';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          event.preventDefault();
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          searchUrl.searchParams.set('type', 'page,article');
          searchUrl.searchParams.set('options[prefix]', 'last');
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[type]', 'page,article');
    url.searchParams.set('resources[limit_scope]', 'each');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    const deferredPromise = SearchUpdateEvent.createPromise();

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: {
          query: searchTerm,
        },
        promise: deferredPromise.promise,
      })
    );

    Promise.all([sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url), faqFind(searchTerm)])
      .then(([resultsMarkup, faqMatches]) => {
        resultsMarkup = faqAugmentSuggestions(resultsMarkup, faqMatches);
        if (!resultsMarkup) {
          deferredPromise.resolve({ totalCount: 0 });
          return;
        }

        if (abortController.signal.aborted) {
          deferredPromise.reject(new Error('Fetch aborted by user'));
          return;
        }

        morph(predictiveSearchResults, resultsMarkup);

        this.#resetScrollPositions();

        // Count all result items (products, collections, pages, articles, queries)
        const resultCount = predictiveSearchResults.querySelectorAll('[ref="resultsItems[]"]').length;
        deferredPromise.resolve({ totalCount: resultCount });
      })
      .catch((error) => {
        deferredPromise.reject(error);
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}

let faqIndexPromise;

function faqSettings() {
  return document.querySelector('predictive-search-component[data-faq-url]');
}

function faqNormalize(value) {
  return value.toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, ' ').trim();
}

function faqAnchor(question) {
  return 'faq-' + faqNormalize(question).replace(/ /g, '-');
}

function faqTokens(value) {
  const aliases = { appointments: 'appointment', appointmetn: 'appointment', appt: 'appointment', booking: 'schedule', book: 'schedule', scheduling: 'schedule', scheduled: 'schedule', request: 'schedule', requesting: 'schedule', meds: 'medication', medications: 'medication', prescriptions: 'medication', prescription: 'medication', refills: 'refill', happens: 'expect', happen: 'expect', cost: 'insurance', coverage: 'insurance', located: 'location', address: 'location', hours: 'hour', effects: 'effect', sessions: 'therapy' };
  const stop = new Set('a an and are as at be been before by can could do does for from get have how i in into is it me my of on or our please so that the their them there these they this to us was we what when where which who why will with would you your during about need want find know'.split(' '));
  return [...new Set(faqNormalize(value).replace(/make (?:an? )?appoint[a-z]*/g, 'schedule appointment').split(' ').filter(word => word.length > 1 && !stop.has(word)).map(word => aliases[word] || word))];
}

function faqTokenMatch(a, b) {
  if (a === b) return true;
  if (a.length >= 4 && (a.startsWith(b) || b.startsWith(a))) return true;
  if (a.length < 5 || Math.abs(a.length - b.length) > 1) return false;
  if (a.length === b.length) {
    const differences = [];
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) differences.push(i);
    if (differences.length === 1) return true;
    return differences.length === 2 && differences[1] === differences[0] + 1 && a[differences[0]] === b[differences[1]] && a[differences[1]] === b[differences[0]];
  }
  const shorter = a.length < b.length ? a : b;
  const longer = a.length < b.length ? b : a;
  for (let i = 0; i < longer.length; i++) if (longer.slice(0, i) + longer.slice(i + 1) === shorter) return true;
  return false;
}

function faqExtract(root) {
  return Array.from(root.querySelectorAll('.bb-faq-item')).map(item => {
    const question = item.querySelector('.bb-faq-question')?.textContent?.replace(/\s+/g, ' ').trim() || '';
    const answer = item.querySelector('.bb-faq-answer')?.textContent?.replace(/\s+/g, ' ').trim() || '';
    return { question, answer, anchor: faqAnchor(question), questionTokens: faqTokens(question), tokens: faqTokens(question + ' ' + answer) };
  }).filter(item => item.question && item.answer);
}

async function faqLoad() {
  const path = faqSettings()?.getAttribute('data-faq-url');
  if (!path) return [];
  if (!faqIndexPromise) {
    faqIndexPromise = (async () => {
      const url = new URL(path, location.origin);
      if (url.origin !== location.origin) return [];
      if (location.pathname === url.pathname) return faqExtract(document);
      const response = await fetch(url, { credentials: 'same-origin', signal: AbortSignal.timeout(5000) });
      if (!response.ok) return [];
      return faqExtract(new DOMParser().parseFromString(await response.text(), 'text/html'));
    })().catch(() => { faqIndexPromise = null; return []; });
  }
  return faqIndexPromise;
}

async function faqFind(query) {
  const terms = faqTokens(query);
  if (!terms.length) return [];
  const index = await faqLoad();
  return index.map(item => {
    const matched = terms.filter(term => item.tokens.some(word => faqTokenMatch(term, word))).length;
    const inQuestion = terms.filter(term => item.questionTokens.some(word => faqTokenMatch(term, word))).length;
    const required = terms.length <= 2 ? terms.length : Math.ceil(terms.length * 0.7);
    return { ...item, score: matched >= required ? inQuestion * 6 + matched * 2 + (faqNormalize(item.question).includes(faqNormalize(query)) ? 10 : 0) : 0 };
  }).filter(item => item.score > 0).sort((a, b) => b.score - a.score);
}

function faqResultSection(matches, predictive = false) {
  const section = document.createElement('section');
  section.className = 'bb-faq-search' + (predictive ? ' bb-faq-search--predictive' : '');
  const heading = document.createElement(predictive ? 'h4' : 'h2');
  heading.textContent = faqSettings()?.getAttribute('data-faq-title') || 'FAQ';
  section.append(heading);
  const list = document.createElement('ul');
  list.className = predictive ? 'bb-faq-search__list predictive-search-results__list list-unstyled' : 'bb-faq-search__list';
  for (const match of matches) {
    const item = document.createElement('li');
    item.className = 'bb-faq-search__item';
    if (predictive) item.setAttribute('ref', 'resultsItems[]');
    const title = document.createElement(predictive ? 'div' : 'h3');
    const link = document.createElement('a');
    const url = new URL(faqSettings().getAttribute('data-faq-url'), location.origin);
    url.hash = match.anchor;
    link.href = url.pathname + url.search + url.hash;
    link.textContent = match.question;
    title.append(link);
    const answer = document.createElement('p');
    answer.textContent = predictive && match.answer.length > 220 ? match.answer.slice(0, 220).replace(/\s+\S*$/, '') + '…' : match.answer;
    item.append(title, answer);
    list.append(item);
  }
  section.append(list);
  return section;
}

function faqAugmentSuggestions(markup, matches) {
  if (!matches.length) return markup;
  const doc = new DOMParser().parseFromString(markup, 'text/html');
  const inner = doc.querySelector('.predictive-search-results__inner');
  if (!inner) return markup;
  inner.querySelectorAll('.predictive-search-results__no-results, [data-single-result-url]').forEach(node => node.remove());
  inner.prepend(faqResultSection(matches.slice(0, 5), true));
  const status = inner.querySelector('[role="status"]');
  if (status) status.textContent = inner.querySelectorAll('[ref="resultsItems[]"]').length + ' ' + (faqSettings()?.getAttribute('data-results-label') || '');
  return doc.body.innerHTML;
}

async function faqShowPageResults() {
  const results = document.querySelector('.content-search-results .collection-wrapper');
  if (!results || results.querySelector('.bb-faq-search')) return;
  const query = new URLSearchParams(location.search).get('q') || '';
  const matches = await faqFind(query);
  if (!matches.length || !results.isConnected) return;
  results.prepend(faqResultSection(matches));
  const empty = document.querySelector('.search-results__no-results');
  if (empty) empty.hidden = true;
  document.title = query + ' – ' + (faqSettings()?.getAttribute('data-results-label') || document.title);
}

function faqOpenAnswer() {
  const hash = location.hash.slice(1);
  if (!hash.startsWith('faq-')) return;
  const item = document.getElementById(hash);
  const button = item?.querySelector('.bb-faq-question');
  if (!button) return;
  if (button.getAttribute('aria-expanded') !== 'true') button.click();
  requestAnimationFrame(() => {
    button.focus({ preventScroll: true });
    item.scrollIntoView({ block: 'center', behavior: 'instant' });
  });
}

function faqInitialize() {
  document.querySelectorAll('.bb-faq-item').forEach(item => {
    const question = item.querySelector('.bb-faq-question')?.textContent?.trim();
    if (question) item.id = faqAnchor(question);
  });
  faqOpenAnswer();
  faqShowPageResults();
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', faqInitialize, { once: true });
else faqInitialize();
window.addEventListener('hashchange', faqOpenAnswer);
window.addEventListener('load', faqOpenAnswer, { once: true });
